import ShipmentsIcon from '@mui/icons-material/AirplanemodeActiveOutlined';
import CasesOutlined from '@mui/icons-material/CasesOutlined';
import ComputerOutlined from '@mui/icons-material/ComputerOutlined';
import ConnectWithoutContactOutlined from '@mui/icons-material/ConnectWithoutContactOutlined';
import CorporateFareOutlined from '@mui/icons-material/CorporateFareOutlined';
import DesignServicesOutlined from '@mui/icons-material/DesignServicesOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import LogOutIcon from '@mui/icons-material/LogoutOutlined';
import MonetizationOnOutlined from '@mui/icons-material/MonetizationOnOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import QuestionAnswerOutlined from '@mui/icons-material/QuestionAnswerOutlined';
import BillingIcon from '@mui/icons-material/ReceiptOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StoreIcon from '@mui/icons-material/Storefront';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { Form } from '@remix-run/react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { MuiRemixLink } from '~/components/shared/links';
import { HomeIcon, InventoryIcon, OrdersIcon, RecipientsIcon } from '~/routes/dashboard';

type CartButtonProps = {
  totalProducts: number;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
};
export function CartButton({ totalProducts, setOpenCart }: CartButtonProps) {
  return (
    <IconButton
      color="inherit"
      aria-label="cart"
      aria-haspopup="true"
      onClick={() => setOpenCart((prev) => !prev)}
      sx={{ p: 0.75 }}
    >
      <Badge badgeContent={totalProducts} sx={{ '& .MuiBadge-badge': { bgcolor: 'secondary.main' } }}>
        <ShoppingCartOutlinedIcon sx={{ color: 'primary.dark' }} />
      </Badge>
    </IconButton>
  );
}

export function Logout({ showIcon }: { showIcon: boolean }) {
  const { t } = useTranslation('root');

  return (
    <Form
      method="post"
      action="/logout"
      style={{ width: '100%', display: 'flex', alignItems: 'center', paddingLeft: '24px' }}
    >
      {showIcon && <LogOutIcon />}
      <Button
        type="submit"
        variant="text"
        color="inherit"
        disableRipple
        aria-label="log out"
        fullWidth={true}
        sx={{ '&:hover': { bgcolor: 'transparent' }, justifyContent: 'flex-start', pl: '12px' }}
      >
        {t('logOut')}
      </Button>
    </Form>
  );
}

export function MenuEntry({ entry, href, icon }: { entry: string; href: string; icon?: JSX.Element }) {
  const { t } = useTranslation('root');

  return (
    <MuiRemixLink
      href={href}
      underline="none"
      color="inherit"
      aria-label={entry}
      pl={3}
      pr={2}
      py={0.75}
      width="100%"
      display="flex"
      alignItems="center"
    >
      {icon}
      <Typography variant="button" ml="12px">
        {t(entry)}
      </Typography>
    </MuiRemixLink>
  );
}

export function NavbarIcon({ icon }: { icon: any }) {
  return (
    <Box width={24} height={24} bgcolor="#000" color="#FFF" borderRadius="50%">
      {icon}
    </Box>
  );
}

export enum ModalMenuId {
  Solution = 1,
  Company,
  Shop,
  Profile,
  PackBuilder,
  Account,
  Dashboard,
  Divider,
  AccountSettings,
  DesignLab,
}

export const menuItemList = [
  {
    label: 'Shop',
    type: 'menu',
    modalId: ModalMenuId.Shop,
    needPadding: true,
    showInDesktopView: true,
    userLogIn: false,
    columns: [
      {
        id: 1,
        links: [
          {
            id: 2,
            icon: <NavbarIcon icon={<Inventory2Outlined sx={{ p: 0.5 }} />} />,
            name: 'Customizable Swag Packs',
            description:
              'A pre-configured selection of our most popular packs for Onboarding, Loyalty, and or any other use case you need.',
            to: '/shop/swag-packs',
          },
          {
            id: 4,
            icon: (
              <NavbarIcon
                icon={
                  <SvgIcon sx={{ p: 0.5 }}>
                    <path
                      xmlns="http://www.w3.org/2000/svg"
                      d="M15.9999 21H7.99992C7.73471 21 7.48035 20.8946 7.29282 20.7071C7.10528 20.5196 6.99992 20.2652 6.99992 20V12.07L5.69992 13.12C5.30992 13.5 4.67992 13.5 4.28992 13.12L1.45992 10.29C1.36722 10.1975 1.29367 10.0876 1.24349 9.96662C1.19331 9.84565 1.16748 9.71597 1.16748 9.585C1.16748 9.45403 1.19331 9.32435 1.24349 9.20338C1.29367 9.0824 1.36722 8.97251 1.45992 8.88L7.33992 3H8.99992C8.99992 4.1 10.3399 5 11.9999 5C13.6599 5 14.9999 4.1 14.9999 3H16.6599L22.5399 8.88C22.9299 9.27 22.9299 9.9 22.5399 10.29L19.7099 13.12C19.3199 13.5 18.6899 13.5 18.2999 13.12L16.9999 12.07V20C16.9999 20.2652 16.8946 20.5196 16.707 20.7071C16.5195 20.8946 16.2651 21 15.9999 21ZM20.4199 9.58L16.1099 5.28C15.7999 5.63 15.4299 5.94 14.9999 6.2C14.1599 6.7 13.1299 7 11.9999 7C10.2999 7 8.78992 6.32 7.88992 5.28L3.57992 9.58L4.99992 11L7.99992 9H8.99992V19H14.9999V9H15.9999L18.9999 11L20.4199 9.58Z"
                    />
                  </SvgIcon>
                }
              />
            ),
            name: 'Customizable Bulk Swag',
            description:
              'Get your message out there with our bulk curated selection that includes backpacks, totes and duffels, apparel, drinkware, notebooks & pens, tech items, and bonus items.',
            to: '/shop/bulk-swag',
          },
          {
            id: 6,
            icon: <NavbarIcon icon={<DesignServicesOutlined sx={{ p: 0.5 }} />} />,
            name: 'Need our professional designers?',
            description:
              'Expert design services for your business.  Elevate your brand, achieve your goals with the help of our professionals.',
            to: '/professional-request',
            externalLink: true,
          },
        ],
      },
    ],
  },
  {
    label: 'Solutions',
    type: 'menu',
    modalId: ModalMenuId.Solution,
    needPadding: true,
    showInDesktopView: true,
    userLogIn: false,
    columns: [
      {
        id: 1,
        title: 'Swag Packs By Use Case',
        links: [
          {
            id: 1,
            icon: <NavbarIcon icon={<CasesOutlined sx={{ p: 0.5 }} />} />,
            name: 'Clients Engagement',
            description:
              'Foster excitement for your brand and the value your team offers with exciting Swag for your clients.',
            to: '/landing/clients-engagement',
          },
          {
            id: 2,
            icon: <NavbarIcon icon={<PersonPinOutlined sx={{ p: 0.5 }} />} />,
            name: 'Employee Onboarding',
            description:
              'Help your new employees feel like they are becoming a part of something greater with custom Swag that signifies they are joining an elite team.',
            to: '/landing/employee-onboarding',
          },
          {
            id: 3,
            icon: <NavbarIcon icon={<ConnectWithoutContactOutlined sx={{ p: 0.5 }} />} />,
            name: 'Corporate Events',
            description:
              'Networking and visibility are fundamental factors in your brand’s success. Give corporate event attendees something to remember your name by with our high-quality Swag.',
            to: '/landing/corporate-events',
          },
          {
            id: 4,
            icon: <NavbarIcon icon={<GroupsOutlined sx={{ p: 0.5 }} />} />,
            name: 'Remote Teams',
            description:
              'For all the cutting-edge flexibility that remote work has to offer, it can be difficult to maintain the human connection. Remind your team what they are a part of with our custom Swag.',
            to: '/landing/remote-teams',
          },
          {
            id: 13,
            icon: <NavbarIcon icon={<ComputerOutlined sx={{ p: 0.5 }} />} />,
            name: 'Remote Events',
            description: 'Our Swag solutions ensure your brand leaves a lasting impression no matter the venue.',
            to: '/landing/remote-events',
          },
          {
            id: 2,
            icon: <NavbarIcon icon={<MonetizationOnOutlined sx={{ p: 0.5 }} />} />,
            name: 'Fundraising',
            description:
              'Exceptional quality Swag items means exceptional profits to fund the impact you want to have.',
            to: '/landing/fundraising',
          },
          {
            id: 3,
            icon: <NavbarIcon icon={<SchoolOutlined sx={{ p: 0.5 }} />} />,
            name: 'Schools And Colleges',
            description: 'Embody school pride and spirit with custom Swag that shows the world what you’re made of.',
            to: '/landing/schools-and-colleges',
          },
          {
            id: 4,
            icon: <NavbarIcon icon={<EventAvailableOutlined sx={{ p: 0.5 }} />} />,
            name: 'In-person Events',
            description: 'Make any event memorable with exceptionally high-quality Swag for guests and attendees.',
            to: '/landing/in-person-events',
          },
        ],
      },
      {
        id: 3,
        title: 'Swag By Roles',
        links: [
          {
            id: 14,
            icon: <NavbarIcon icon={<PeopleOutlined sx={{ p: 0.5 }} />} />,
            name: 'Human Resource Specialist',
            description:
              'Human Resource Specialists are your go-to champion on the front line of successful brand and employee relations. Arm them with outstanding Swag that boosts company pride and excites go-getters.',
            to: '/landing/hr-specialist',
          },
          {
            id: 2,
            icon: <NavbarIcon icon={<Inventory2Outlined sx={{ p: 0.5 }} />} />,
            name: 'Office Managers',
            description: 'Give your Office Managers the Swag they need to push the tempo and motivate success.',
            to: '/landing/office-managers',
          },
          {
            id: 3,
            icon: <NavbarIcon icon={<InventoryOutlined sx={{ p: 0.5 }} />} />,
            name: 'Events Organizers',
            description:
              'Show your appreciation and help Events Organizers stand out with custom Swag that will transform any event into an immersive brand experience.',
            to: '/landing/events-organizers',
          },
          {
            id: 4,
            icon: <NavbarIcon icon={<HomeOutlined sx={{ p: 0.5 }} />} />,
            name: 'NGOs And Non-Profits',
            description:
              'Nothing spreads awareness and inspires confidence like bespoke Swag tailored to your impactful charity and mission.',
            to: '/landing/ngos-and-non-profit',
          },
          {
            id: 5,
            icon: <NavbarIcon icon={<CorporateFareOutlined sx={{ p: 0.5 }} />} />,
            name: 'Program Coordinators',
            description:
              'Program Coordinators drive your company milestones. Give them the Swag they need to commemorate, motivate, and electrify your brand’s future.',
            to: '/landing/program-coordinators',
          },
        ],
      },
    ],
  },
  {
    label: 'Company',
    type: 'menu',
    modalId: ModalMenuId.Company,
    needPadding: false,
    showInDesktopView: true,
    userLogIn: false,
    columns: [
      {
        id: 1,
        links: [
          {
            id: 1,
            icon: (
              <NavbarIcon
                icon={
                  <SvgIcon viewBox="0 0 16 16" sx={{ p: 0.5 }}>
                    <path d="M15.9038 4.5462L11.4191 6.96854L8.18609 8.75579L8.18507 15.9575L15.9038 11.7154V4.5462ZM12.0196 6.05579L15.6408 4.09981L12.0292 2.12201L12.0196 6.05579ZM3.5644 5.89241L7.62725 3.61695V0.0375404L0.260578 4.09965L3.5644 5.89241ZM11.4609 6.35752L11.4713 2.12088L8.04947 3.97386L3.84529 6.32846V13.6478L7.62791 11.5629L7.62827 8.47262L11.1309 6.53583L11.4609 6.35752ZM0 4.547L3.28747 6.33112V13.6514L0 11.8678V4.547Z" />
                  </SvgIcon>
                }
              />
            ),
            name: 'About Us',
            description:
              'Get to know us and our mission to bring you the highest-quality Swag products that will transform your brand, image, or organization.',
            to: 'about-us',
          },
          {
            id: 3,
            icon: <NavbarIcon icon={<QuestionAnswerOutlined sx={{ p: 0.5 }} />} />,
            name: 'FAQ’s',
            description: 'Get answers to your questions about our amazing products and services.',
            to: '/faq',
          },
        ],
      },
    ],
  },
  {
    label: 'Pack Builder',
    type: 'link',
    modalId: ModalMenuId.PackBuilder,
    needPadding: false,
    showInDesktopView: true,
    userLogIn: false,
    href: '/guided-pack-builder',
  },
  {
    label: 'Design Lab',
    type: 'link',
    modalId: ModalMenuId.DesignLab,
    needPadding: false,
    showInDesktopView: true,
    userLogIn: false,
    href: '/design-lab',
  },
  {
    type: 'divider',
    modalId: ModalMenuId.Divider,
    showInDesktopView: false,
    userLogIn: true,
  },
  {
    label: 'Dashboard',
    type: 'menu',
    modalId: ModalMenuId.Dashboard,
    needPadding: false,
    showInDesktopView: false,
    userLogIn: true,
    columns: [
      {
        id: 1,
        links: [
          {
            id: 0,
            icon: <HomeIcon width={20} height={20} />,
            name: 'Dashboard',
            to: '/dashboard',
          },
          {
            id: 1,
            icon: <DesignServicesOutlined width={20} height={20} />,
            name: 'My products',
            to: '/dashboard/my-products',
          },
          {
            id: 7,
            icon: <StoreIcon />,
            name: 'Stores',
            to: '/dashboard/stores',
            flag: 'enable-web-stores',
          },
          {
            id: 2,
            icon: <OrdersIcon width={20} height={20} />,
            name: 'Orders',
            to: '/dashboard/orders',
          },
          {
            id: 3,
            icon: <InventoryIcon width={20} height={20} />,
            name: 'Inventory',
            to: '/dashboard/inventory',
          },
          {
            id: 4,
            icon: <RecipientsIcon width={20} height={20} />,
            name: 'Recipients',
            to: '/dashboard/recipients',
          },
          {
            id: 5,
            icon: <ShipmentsIcon width={20} height={20} />,
            name: 'Shipments',
            to: '/dashboard/shipments',
          },
          {
            id: 6,
            icon: <BillingIcon width={20} height={20} />,
            name: 'Invoices',
            to: '/dashboard/billing',
          },
        ],
      },
    ],
  },
  {
    label: 'Manage Accounts',
    type: 'link',
    modalId: ModalMenuId.Account,
    needPadding: false,
    showInDesktopView: false,
    userLogIn: true,
    href: '/dashboard/manage-accounts',
  },
  {
    label: 'Account Settings',
    type: 'link',
    modalId: ModalMenuId.AccountSettings,
    needPadding: false,
    showInDesktopView: false,
    userLogIn: true,
    href: '/dashboard/account-settings/details',
  },
  {
    label: 'Profile Settings',
    type: 'link',
    modalId: ModalMenuId.Profile,
    needPadding: false,
    showInDesktopView: false,
    userLogIn: true,
    href: '/dashboard/profile',
  },
];
